import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { BtcGameAsset } from '@optools/services/operator-gateway/btc/types';
import {
  PipelineStatus,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { ReactNode, useMemo } from 'react';

import { useAssetMutations } from '../hooks/useAssetMutations';
import { AdStatusSwitch } from './ad-status-switch';
import { AssetStateSelect } from './asset-state-select';

export interface PipelineAssetProps {
  assets: BtcGameAsset[];
  children?: ReactNode;
  className?: string;
  enableRowSelection?: boolean;
  gamePk: number;
  handleRowClick?: (rowIndex: number) => void;
  mediaId: string;
  onSelectPlayback?: (asset: BtcGameAsset, mediaId: string) => void;
  pipelineStatus: PipelineStatus;
  rowSelection?: RowSelectionState;
  streamState: StreamState;
}

export function PipelineAssets({
  assets,
  children,
  className,
  enableRowSelection,
  gamePk,
  handleRowClick,
  mediaId,
  onSelectPlayback,
  pipelineStatus,
  rowSelection,
  streamState,
}: PipelineAssetProps) {
  const {
    handleIngestIntoDai,
    handleStateChange,
    handleToggleAdStatus,
    isAdStatusPending,
    isStatePending,
    pendingAssetId,
  } = useAssetMutations({ gamePk, mediaId });

  const columns = useMemo<ColumnDef<BtcGameAsset>[]>(() => {
    const cols: ColumnDef<BtcGameAsset>[] = [
      {
        accessorKey: 'assetId',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Asset ID" />,
        id: 'Asset ID',
      },
      {
        accessorKey: 'cdn',
        header: ({ column }) => <DataTableColumnHeader column={column} title="CDN" />,
        id: 'CDN',
      },
      {
        accessorKey: 'provider',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Provider" />,
      },
      {
        accessorKey: 'tier',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Tier" />,
      },
      {
        accessorFn: (row) => row.features?.join(' '),
        accessorKey: 'features',
        cell: ({ cell }) => {
          return cell.row.original.features?.length ? (
            <div className="flex flex-wrap gap-1">
              {cell.row.original.features.map((feature) => (
                <Badge key={feature} variant="secondary">
                  {feature}
                </Badge>
              ))}
            </div>
          ) : null;
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Features" />,
        size: 300,
      },
      {
        cell: ({ cell }) => {
          const assetPending = isAdStatusPending && cell.row.original.assetId === pendingAssetId;
          return (
            <AdStatusSwitch
              asset={cell.row.original}
              isPending={assetPending}
              pipelineStatus={pipelineStatus}
              streamState={streamState}
              onIngestIntoDai={handleIngestIntoDai}
              onToggleAdStatus={handleToggleAdStatus}
            />
          );
        },
        header: 'Ad Status',
        id: 'adStatus',
        meta: {
          cellClassName: 'has-[>button]:flex has-[>button]:justify-center text-center',
          headerClassName: 'text-center',
        },
      },
      {
        cell: ({ cell }) => {
          const assetPending = isStatePending && cell.row.original.assetId === pendingAssetId;
          return (
            <div className="min-w-36">
              <AssetStateSelect
                asset={cell.row.original}
                isPending={assetPending}
                onStateChange={handleStateChange}
              />
            </div>
          );
        },
        header: 'State',
        id: 'state',
      },
    ];

    if (onSelectPlayback) {
      cols.push({
        cell: ({ cell }) => (
          <Button size="sm" onClick={() => onSelectPlayback(cell.row.original, mediaId)}>
            Playback Media
          </Button>
        ),
        header: 'Playback',
        id: 'playback media',
      });
    }

    return cols;
  }, [
    handleIngestIntoDai,
    handleStateChange,
    handleToggleAdStatus,
    isAdStatusPending,
    isStatePending,
    mediaId,
    onSelectPlayback,
    pendingAssetId,
    pipelineStatus,
    streamState,
  ]);

  if (!assets.length) {
    return (
      <div className="flex h-48 w-full items-center justify-center border border-dashed">
        <p>No Assets</p>
      </div>
    );
  }

  return (
    <DataTable
      className={className}
      columns={columns}
      data={assets}
      enableRowSelection={enableRowSelection}
      handleRowClick={handleRowClick}
      pagination={false}
      rowSelection={rowSelection}>
      {children}
    </DataTable>
  );
}
